import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './DateTimePicker.css';
import { Button, Popover, PopoverContent, PopoverHandler } from '@material-tailwind/react';
import { DatePickerInput, DateTimeSection } from './Components';
import { Time } from './Components/TimeSection';
import moment from 'moment';
import { FaLockOpen } from 'react-icons/fa6';
import { IoLockClosed } from 'react-icons/io5';

type ValuePiece = Date | null;

const LOCAL_STORAGE_KEY = 'lockedDates';

export interface DateRange {
  startDate: ValuePiece;
  endDate: ValuePiece;
}

type Placement =
  | 'left-start'
  | 'left-end'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end';

interface DateTimePickerProps {
  id: string;
  onDateRangeChange: (dateRange: DateRange) => void;
  onClearSelection: () => void;
  initialDateRange?: DateRange;
  placement?: Placement;
  showOnlyDate?: boolean;
  lockable?: boolean;
  handlerWidth?: string;
}

const addIdsToDays = ({ date }: any) => {

  // Format the date using Moment.js, in the browser's time zone
  const formattedDate = moment(date).format('YYYY-MM-DD');

  // Generate the ID with the required format
  const id = `day-${formattedDate}`;
  return <span id={id} />;
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  id,
  onDateRangeChange,
  onClearSelection,
  initialDateRange,
  placement,
  showOnlyDate,
  lockable = false,
  handlerWidth = '365px',
}) => {
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const [open, setOpen] = useState(false);
  const [is24HourFormat, setIs24HourFormat] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Set the initial date range if provided
  useEffect(() => {
    const storedData = localStorage.getItem(`${LOCAL_STORAGE_KEY}-${id}`);
    if (storedData) {
      const { startDate, endDate } = JSON.parse(storedData);
      setDateRange({
        startDate: startDate ? new Date(startDate) : null,
        endDate: endDate ? new Date(endDate) : null,
      });
      setIsLocked(true);
      return;
    } else {
      setDateRange(initialDateRange || { startDate: null, endDate: null });
    }
  }, [id, initialDateRange]);

  useEffect(() => {
    const is24Hour = checkIf24HourFormat();
    setIs24HourFormat(is24Hour);
  }, []);

  const checkIf24HourFormat = () => {
    const formatter = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
    });
    const formattedTime = formatter.format(new Date());
    return !formattedTime.includes('AM') && !formattedTime.includes('PM');
  };

  const handleDateChange = (dates: ValuePiece[] | ValuePiece) => {
    if (isLocked) return;

    if (Array.isArray(dates)) {
      const [startDate, endDate] = dates;
      startDate?.setHours(0, 0, 0, 0);
      endDate?.setHours(23, 59, 59);
      setDateRange({ startDate, endDate });
      // Update the state to rerender the time select component
      setShouldUpdate((prev) => !prev);
    } else {
      setDateRange({ startDate: null, endDate: null });
    }
  };

  const toggleLock = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the button from toggling the popover
    if (isLocked) {
      localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${id}`);
      setIsLocked(false);
    } else {
      localStorage.setItem(`${LOCAL_STORAGE_KEY}-${id}`, JSON.stringify(dateRange));
      setIsLocked(true);
    }
  };

  const handleTimeChange = (time: Time, field: 'startDate' | 'endDate') => {
    const selectedTime = new Date(dateRange[field] || new Date());
    selectedTime.setHours(parseInt(time.hour, 10), parseInt(time.minute, 10));
    setDateRange({ ...dateRange, [field]: selectedTime });
  };

  const handleDateTimeQuery = () => {
    onDateRangeChange(dateRange);
    setOpen(false);
  };

  const handleClearSelection = (type: 'clearButton' | 'default', event?: React.MouseEvent<HTMLButtonElement>) => {
    if (type === 'clearButton' && event) {
      event.stopPropagation();
    }
    if (open) {
      setOpen(false);
    }
    setDateRange({ startDate: null, endDate: null });
    onClearSelection();
  };

  const handlePopoverOpen = () => {
    setOpen((prev) => !prev);
  };

  return (
    <Popover placement={placement || 'bottom'} open={open} handler={handlePopoverOpen}>
      <PopoverHandler>
        <button
          id={id}
          style={{ width: handlerWidth }}
          onClick={handlePopoverOpen}
          className="flex items-center justify-between border p-3 rounded-md bg-white border-neutral-grey100 shadow-sm text-neutral-grey800 gap-1.5"
        >
          {lockable && (
            <div className="flex items-center gap-3">
              <button onClick={toggleLock}>
                {isLocked ? <IoLockClosed className="text-blue-500" /> : <FaLockOpen className="text-gray-400" />}
              </button>
              {/* Divider */}
              <div className="w-px h-6 bg-gray-300"></div>
            </div>
          )}
          <DatePickerInput
            id={id}
            isLocked={isLocked}
            dateRange={dateRange}
            handleClearSelection={(e: React.MouseEvent<HTMLButtonElement>) => handleClearSelection('clearButton', e)}
            timeFormat={is24HourFormat ? '24' : '12'}
          />
        </button>
      </PopoverHandler>
      <PopoverContent 
        className="z-[99999]"
        placeholder=""
        onResize={() => {}}
        onResizeCapture={() => {}}
        onPointerEnterCapture={() => {}}
        onPointerLeaveCapture={() => {}}
      >
        <div>
          {/* Calendar component */}
          <Calendar
            onChange={handleDateChange}
            value={[dateRange.startDate, dateRange.endDate]}
            selectRange
            showDoubleView
            locale="en-US"
            tileContent={addIdsToDays}
          />
          {/* Date Time Components */}
          <div className="flex gap-4 -mt-3">
            {dateRange.startDate && (
              <DateTimeSection
                id={`${id}-start-time`}
                date={dateRange.startDate}
                title="From"
                onTimeChange={(time) => handleTimeChange(time, 'startDate')}
                shouldUpdate={shouldUpdate}
                timeFormat={is24HourFormat ? '24' : '12'}
                disableTime={showOnlyDate}
                isLocked={isLocked}
              />
            )}
            {dateRange.endDate && (
              <DateTimeSection
                id={`${id}-end-time`}
                date={dateRange.endDate}
                title="To"
                onTimeChange={(time) => handleTimeChange(time, 'endDate')}
                shouldUpdate={shouldUpdate}
                timeFormat={is24HourFormat ? '24' : '12'}
                disableTime={showOnlyDate}
                isLocked={isLocked}
              />
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end mt-4 gap-3">
            <Button
              id={`${id}-btn-cancel`}
              variant="outlined"
              disabled={isLocked}
              size="sm"
              color="blue"
              className="bg-white text-blue-500 focus:bg-white outline-1 px-6 py-2.5 hover:opacity-100 focus:ring-blue-500 focus:ring-opacity-10"
              onClick={() => handleClearSelection('default')}
              placeholder=""
              onResize={() => {}}
              onResizeCapture={() => {}}
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
            >
              Cancel
            </Button>
            <Button
              id={`${id}-btn-apply`}
              size="sm"
              variant="outlined"
              className="bg-blue-500 focus:bg-blue-500 border-none outline-none px-6 py-2.5 text-white hover:opacity-100 focus:ring-blue-500 focus:ring-opacity-10"
              disabled={!dateRange.endDate && !dateRange.startDate}
              onClick={handleDateTimeQuery}
              placeholder=""
              onResize={() => {}}
              onResizeCapture={() => {}}
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateTimePicker; 